/* eslint-disable */
import { useState, useEffect, useRef } from "react";
import { COLOR, FONT } from "../utils/theme";
import { Icon } from "../utils/icons";

// Duplicated from DayPicker — small pure functions for city block logic
function getBaseCity(city) {
  if (!city) return "";
  if (city.includes("\u2192")) return city.split("\u2192")[0].trim();
  return city;
}

function getDayGroupCity(day, index, allDays) {
  const baseCity = getBaseCity(day.city);
  // Only fold day trips (city contains →) into neighbor block
  if (day.city && day.city.includes("\u2192")) {
    const prevCity = index > 0 ? getBaseCity(allDays[index - 1].city) : null;
    const nextCity = index < allDays.length - 1 ? getBaseCity(allDays[index + 1].city) : null;
    if (prevCity && nextCity && prevCity === nextCity) {
      return prevCity;
    }
  }
  return baseCity;
}

function Row({ icon, iconText, label, disabled, danger, onClick }) {
  const color = danger ? COLOR.mauveDark : COLOR.inkDark;
  return (
    <button
      onClick={disabled ? undefined : onClick}
      onMouseEnter={disabled ? undefined : (e) => { e.currentTarget.style.background = COLOR.warmSurface; }}
      onMouseLeave={disabled ? undefined : (e) => { e.currentTarget.style.background = "transparent"; }}
      style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "9px 16px", cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.3 : 1,
        background: "transparent", border: "none", width: "100%",
        fontFamily: FONT.mono, fontSize: 13, color,
        textAlign: "left",
      }}
    >
      {icon && <Icon name={icon} size={16} color={disabled ? COLOR.inkLight : color} />}
      {iconText && <span style={{ width: 16, textAlign: "center", fontSize: 16, color, lineHeight: 1 }}>{iconText}</span>}
      {label}
    </button>
  );
}

export default function DayContextMenu({ day, days, position, onMoveLeft, onMoveRight, onAddDayBefore, onAddDayAfter, onDeleteDay, onClose }) {
  const menuRef = useRef(null);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  // Close on click outside (delayed to avoid same-event close)
  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose();
    };
    const timer = setTimeout(() => {
      window.addEventListener("mousedown", handleClick);
      window.addEventListener("touchstart", handleClick);
    }, 10);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("mousedown", handleClick);
      window.removeEventListener("touchstart", handleClick);
    };
  }, [onClose]);

  // City block boundary logic using getDayGroupCity
  const dayIdx = days.findIndex(d => d.id === day.id);
  const groupCity = dayIdx >= 0 ? getDayGroupCity(day, dayIdx, days) : "";
  let blockStart = dayIdx;
  let blockEnd = dayIdx;
  while (blockStart > 0 && getDayGroupCity(days[blockStart - 1], blockStart - 1, days) === groupCity) blockStart--;
  while (blockEnd < days.length - 1 && getDayGroupCity(days[blockEnd + 1], blockEnd + 1, days) === groupCity) blockEnd++;

  const canMoveLeft = dayIdx > blockStart;
  const canMoveRight = dayIdx < blockEnd;

  // Edge detection — measure actual menu size after render for accurate bounds
  const [adjustedPos, setAdjustedPos] = useState({ left: position.x, top: position.y });
  useEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const mw = rect.width || 200;
    const mh = rect.height || 240;
    let l = position.x;
    let t = position.y;
    if (l + mw > window.innerWidth - 8) l = Math.max(8, window.innerWidth - mw - 8);
    if (l < 8) l = 8;
    if (t + mh > window.innerHeight - 8) t = Math.max(8, window.innerHeight - mh - 8);
    if (t < 8) t = 8;
    setAdjustedPos({ left: l, top: t });
  }, [position.x, position.y]);
  const left = adjustedPos.left;
  const top = adjustedPos.top;

  const fire = (handler, disabled) => {
    if (disabled) return;
    handler();
    onClose();
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 500 }}>
      <div
        ref={menuRef}
        style={{
          position: "absolute", left, top,
          background: COLOR.white, borderRadius: 10,
          boxShadow: "0 4px 20px rgba(44,24,16,0.15)",
          padding: "6px 0", minWidth: 180, zIndex: 501,
        }}
      >
        <Row icon="back" label="Move earlier" disabled={!canMoveLeft} onClick={() => fire(onMoveLeft, !canMoveLeft)} />
        <Row icon="forward" label="Move later" disabled={!canMoveRight} onClick={() => fire(onMoveRight, !canMoveRight)} />
        <div style={{ height: 1, background: COLOR.borderWarm, margin: "4px 0" }} />
        <Row iconText="+" label="Add day before" onClick={() => fire(onAddDayBefore, false)} />
        <Row iconText="+" label="Add day after" onClick={() => fire(onAddDayAfter, false)} />
        <div style={{ height: 1, background: COLOR.borderWarm, margin: "4px 0" }} />
        <Row icon="delete" label="Delete day" danger onClick={() => fire(onDeleteDay, false)} />
      </div>
    </div>
  );
}
